import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Change, Client, Project } from '../../models';
import {
    useAppSelector,
    useAppDispatch,
    createChange,
    loadActiveTrack,
    createTrack,
    stopActiveTrack,
    updateChange,
} from '../../redux';
import { Changes, Comments } from '../../services';
import { Icon, Tooltip, TrackClientPromptModal, FinishedTrackPromptModal } from '..';
import { hasExplicitClient, resolveTrackClient } from '../../utils/resolveTrackClient';
import { spawnLinkedChange } from '../../utils/createLinkedChange';

interface ChangeTrackProps {
    change: Change;
    className?: string;
}

type FinishedTrackAction = 'switch' | 'create';

function ChangeTrack(props: ChangeTrackProps) {
    const { change, className } = props;

    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { user } = useAppSelector(state => state.auth);
    const { projects } = useAppSelector(state => state.projects);
    const { active, loadingActive, stoppingActive, creating } = useAppSelector(state => state.tracks);
    const [promptOpen, setPromptOpen] = useState(false);
    const [finishedPromptOpen, setFinishedPromptOpen] = useState(false);
    const [suiteBusy, setSuiteBusy] = useState(false);
    const pendingFinishedAction = useRef<FinishedTrackAction | null>(null);

    const buildTitleFor = (target: Change) => {
        let title = `${target.slugName()} - ${target.localizedTitle(i18n.language)}`;
        if (target.isInReview(/*onlyByTeam*/true) && user?._id !== target.assignee?._id)
            title = '[Review] ' + title;
        return title;
    };

    const assignClientToChangeIfNeeded = (target: Change, client: Client | AnyObj | string) => {
        if (!target?._id || hasExplicitClient(target))
            return;
        dispatch(updateChange({ _id: target._id, client }, /*patch*/true, /*optimistic*/true));
    };

    const startTrackOnChange = (target: Change, client: Client | AnyObj | string) => {
        assignClientToChangeIfNeeded(target, client);
        dispatch(createTrack({
            change: target,
            title: buildTitleFor(target),
            project: target.project,
            client,
        }, {}, (err: Error | null) => {
            if (!err) {
                setPromptOpen(false);
                setFinishedPromptOpen(false);
                dispatch(loadActiveTrack());
            }
        }));
    };

    const resolveClientOrPrompt = (action: FinishedTrackAction): Client | AnyObj | string | null => {
        const client = resolveTrackClient(change, projects);
        if (!client) {
            pendingFinishedAction.current = action;
            setPromptOpen(true);
            return null;
        }
        return client;
    };

    const goToChange = (target: Change) => {
        navigate(Project.path(target.project || change.project, 'change', target._id));
    };

    const runSwitchToFollowUp = async (client: Client | AnyObj | string) => {
        const followUpId = change.followUpInto?.change;
        if (!followUpId)
            return;

        setSuiteBusy(true);
        try {
            const data = await Changes.get(followUpId);
            const loaded = data.change;
            goToChange(loaded);
            startTrackOnChange(loaded, client);
        } catch (err) {
            console.error(err);
            window.alert(String(t('changes.finished_track_switch_failed')));
        } finally {
            setSuiteBusy(false);
        }
    };

    const runCreateSuite = async (client: Client | AnyObj | string) => {
        setSuiteBusy(true);
        try {
            const projectId = (typeof change.project === 'object' && change.project)
                ? change.project._id
                : change.project;
            const project = (projects || []).find((p: Project) => p._id === projectId) || null;

            const createdRaw = await spawnLinkedChange({
                source: change,
                titlePrefix: String(t('changes.create_linked_change_title_prefix') || 'Suite : '),
                createdLabel: String(t('changes.create_linked_change_created_comment') || 'Suite créée :'),
                sourceLabel: String(t('changes.create_linked_change_source_comment') || 'Suite de'),
                board: null,
                project,
                createChange: (payload, params) => new Promise<Change>((resolve, reject) => {
                    dispatch(createChange(payload, params, (err, result) => {
                        if (err || !result)
                            reject(err || new Error(String(t('changes.create_linked_change_failed'))));
                        else
                            resolve(result as Change);
                    }));
                }),
                addRelated: (sourceId, relatedId) => Changes.addRelated(sourceId, relatedId),
                setFollowUp: (sourceId, targetId) => Changes.setFollowUp(sourceId, targetId),
                postComment: (body) => Comments.create(body).then((data: AnyObj) => {
                    if (!data?.comment)
                        throw new Error(String(t('changes.create_linked_change_failed')));
                    return data.comment;
                }),
            });

            const created = createdRaw instanceof Change ? createdRaw : new Change(createdRaw);
            goToChange(created);
            startTrackOnChange(created, client);
        } catch (err) {
            console.error(err);
            window.alert(String(t('changes.create_linked_change_failed')));
        } finally {
            setSuiteBusy(false);
        }
    };

    const switchToFollowUpAndTrack = async () => {
        const client = resolveClientOrPrompt('switch');
        if (!client)
            return;
        await runSwitchToFollowUp(client);
    };

    const createSuiteAndTrack = async () => {
        const client = resolveClientOrPrompt('create');
        if (!client)
            return;
        await runCreateSuite(client);
    };

    const startWithClient = (client: Client | AnyObj | string) => {
        const pending = pendingFinishedAction.current;
        pendingFinishedAction.current = null;
        setPromptOpen(false);

        if (pending === 'switch') {
            void runSwitchToFollowUp(client);
            return;
        }
        if (pending === 'create') {
            void runCreateSuite(client);
            return;
        }

        startTrackOnChange(change, client);
    };

    const start = (e: AnyObj) => {
        e.stopPropagation();

        if (change.isFinished() && !change.isMerged()) {
            setFinishedPromptOpen(true);
            return;
        }

        const client = resolveTrackClient(change, projects);
        if (!client) {
            setPromptOpen(true);
            return;
        }

        startTrackOnChange(change, client);
    };

    const stop = (e: AnyObj) => {
        e.stopPropagation();
        dispatch(stopActiveTrack());
    };

    if (!change)
        return null;

    const isLoading = (
        loadingActive === 'pending' ||
        stoppingActive === 'pending' ||
        creating === 'pending' ||
        suiteBusy
    );

    if (!user?.can('track:create'))
        return null;

    const isActive = active?.change?._id === change._id;

    return (
        <div className={`group/track ${className || ''} cursor-pointer inline-flex items-center justify-center`}>
            {!isActive && (
                <Tooltip content={t('changes.help.track_start') || ''} light>
                    <div onClick={start} className="inline-flex items-center justify-center">
                        <Icon type={isLoading ? 'loading' : 'start'} color="blue-500" size={6} />
                    </div>
                </Tooltip>
            )}
            {isActive && (
                <Tooltip content={t('changes.help.track_stop') || ''} light>
                    <div onClick={stop} className="grid h-6 w-6 place-items-center">
                        <Icon
                            type="running"
                            className="col-start-1 row-start-1 group-hover/track:invisible"
                            color="blue-500"
                            size={6}
                        />
                        <Icon
                            type={isLoading ? 'loading' : 'stop'}
                            className="col-start-1 row-start-1 invisible group-hover/track:visible"
                            color="red-500"
                            size={6}
                        />
                    </div>
                </Tooltip>
            )}
            <TrackClientPromptModal
                show={promptOpen}
                loading={creating === 'pending' || suiteBusy}
                onClose={() => {
                    pendingFinishedAction.current = null;
                    setPromptOpen(false);
                }}
                onConfirm={startWithClient}
            />
            <FinishedTrackPromptModal
                show={finishedPromptOpen}
                change={change}
                loading={isLoading}
                onClose={() => setFinishedPromptOpen(false)}
                onSwitchToFollowUp={change.followUpInto?.change ? switchToFollowUpAndTrack : undefined}
                onCreateSuite={createSuiteAndTrack}
            />
        </div>
    );
}

export default ChangeTrack;
